/**
 * @fileoverview Shared utilities for parsing data from various sources (clipboard, CSV, etc.)
 * Consolidates common logic for type coercion, header normalization, and column matching.
 */

function coerceToArray(str: string): string[] {
  if (str === "") return [];

  for (const separator of [";", ",", "|"]) {
    if (str.includes(separator)) {
      return str
        .split(separator)
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }
  }

  return [str];
}

/**
 * Detect number format based on separators and normalize to a
 * plain "1234.56" style string parseable by Number().
 */
function normalizeNumericSeparators(value: string): string {
  if (value.includes(",") && !value.includes(".")) {
    // European: "1234,56" -> "1234.56"
    return value.replace(",", ".");
  }

  if (value.includes(".") && value.includes(",")) {
    // Both present: the last separator is the decimal one
    if (value.lastIndexOf(",") > value.lastIndexOf(".")) {
      // European: "1.234,56" or "-334.444,20" -> "1234.56" or "334444.20"
      return value.replaceAll(".", "").replace(",", ".");
    }
    // American: "1,234.56" or "-667,628.48" -> "1234.56" or "667628.48"
    return value.replaceAll(",", "");
  }

  if (value.includes(",")) {
    // Only commas - could be thousands separator in American format
    // Check if there are 3 digits after comma (thousands) or 2 (decimal)
    const parts = value.split(",");
    if (parts.length === 2 && parts[1].length <= 2) {
      // Likely decimal: "313,41" -> "313.41"
      return value.replace(",", ".");
    }
    // Likely thousands: "1,234" -> "1234"
    return value.replaceAll(",", "");
  }

  return value;
}

function coerceToNumber(str: string): number | undefined {
  if (str === "") return undefined;

  // Remove spaces and non-breaking spaces
  let cleaned = str.replaceAll(/[\s\u00A0\u202F]/g, "");

  // Handle negative numbers with minus at the end (some EU formats)
  let isNegative = false;
  if (cleaned.endsWith("-")) {
    isNegative = true;
    cleaned = cleaned.slice(0, -1);
  } else if (cleaned.startsWith("-")) {
    isNegative = true;
    cleaned = cleaned.slice(1);
  }

  const num = Number(normalizeNumericSeparators(cleaned));
  if (!Number.isFinite(num)) return undefined;

  return isNegative ? -num : num;
}

function coerceToBoolean(str: string): boolean | undefined {
  const lower = str.toLowerCase();
  if (["true", "1", "yes", "ja"].includes(lower)) return true;
  if (["false", "0", "no", "nee"].includes(lower)) return false;
  return undefined;
}

/**
 * Coerce string value to target type.
 * Handles European/American number formats, boolean variations, and semicolon-separated arrays.
 *
 * @param value - The value to coerce
 * @param type - Target type: "text", "number", "boolean", or "array"
 * @returns Coerced value or undefined for invalid conversions
 */
export function coerceValue(
  value: string | number | boolean | null | undefined,
  type?: "text" | "number" | "boolean" | "array",
): unknown {
  if (value === null || value === undefined || value === "") {
    if (type === "boolean") return false;
    if (type === "array") return [];
    return undefined;
  }

  const str = String(value).trim();

  if (type === "array") return coerceToArray(str);
  if (type === "number") return coerceToNumber(str);
  if (type === "boolean") return coerceToBoolean(str);

  // Default: return as text
  return str;
}

/**
 * Normalize header text for fuzzy matching.
 * Removes spaces, underscores, and special characters, converts to lowercase.
 *
 * @param header - Header text to normalize
 * @returns Normalized header string
 */
export function normalizeHeader(header: string): string {
  return header
    .trim()
    .toLowerCase()
    .replaceAll(/[\s_]+/g, "")
    .replaceAll(/[^\w]/g, "");
}

/**
 * Check if a header matches a target using normalization and optional aliases.
 *
 * @param headerText - The actual header text
 * @param targetHeader - The target/expected header
 * @param aliases - Optional array of alias strings to also check
 * @returns True if header matches target or any alias
 */
export function headerMatches(
  headerText: string,
  targetHeader: string,
  aliases?: string[],
): boolean {
  const normalized = normalizeHeader(headerText);
  const targetNormalized = normalizeHeader(targetHeader);

  // Exact match
  if (normalized === targetNormalized) return true;

  // Check aliases
  if (aliases) {
    return aliases.some((alias) => normalizeHeader(alias) === normalized);
  }

  return false;
}

/**
 * Detect if a column key represents a boolean field based on naming conventions.
 *
 * @param dataKey - The data column key/name
 * @returns True if this appears to be a boolean field
 */
export function isBooleanField(dataKey: string): boolean {
  const booleanKeywords = [
    "exclude",
    "include",
    "active",
    "enabled",
    "is_",
    "has_",
  ];
  const normalized = dataKey.toLowerCase();
  return booleanKeywords.some((keyword) => normalized.includes(keyword));
}
